<main class="grow" role="content">
  <div class="kt-container-fixed invoice-detail-container py-5 lg:py-7.5">
    @if (loadError()) {
      <div class="kt-alert kt-alert-destructive mb-5">{{ loadError() }}</div>
    }

    @if (invoice(); as current) {
      <div class="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div class="flex items-center gap-3 flex-wrap">
          <div>
            <h1 class="text-xl font-semibold">Invoice {{ current.invoiceNumber }}</h1>
            <div class="text-sm text-secondary-foreground">Invoices / {{ current.vendorName }}</div>
          </div>
          @if (isEditing()) {
            <span class="invoice-editing-chip">Editing</span>
          }
        </div>

        <div class="inline-flex items-center gap-2">
          <a class="kt-btn kt-btn-outline" [routerLink]="['/invoices']">Back</a>
          @if (!isEditing()) {
            <button type="button" class="kt-btn kt-btn-outline" (click)="startEditing()">Edit Invoice</button>
            <button type="button" class="kt-btn kt-btn-destructive" (click)="deleteInvoice()">Delete Invoice</button>
          }
        </div>
      </div>

      @if (validationMessage()) {
        <div class="kt-alert kt-alert-destructive mb-5">{{ validationMessage() }}</div>
      }

      @for (warning of current.warnings; track warning.code) {
        <div class="kt-alert kt-alert-warning mb-5">{{ warning.message || warning.code }}</div>
      }

      <section class="invoice-sheet">
        <header class="invoice-sheet-header">
          <div class="invoice-brand">
            <div class="invoice-brand-mark">INV</div>
            <div>
              <div class="invoice-brand-title">Invoice</div>
              <div class="invoice-brand-sub">Frida Treasury Management</div>
            </div>
          </div>

          <div class="invoice-meta">
            <div class="invoice-meta-row">
              <span class="invoice-meta-label">Invoice #</span>
              @if (isEditing()) {
                <input
                  type="text"
                  class="kt-input invoice-meta-input"
                  [ngModel]="form().invoiceNumber"
                  (ngModelChange)="onFieldChange('invoiceNumber', $event)"
                />
              } @else {
                <div class="invoice-readonly-value">{{ current.invoiceNumber }}</div>
              }
            </div>
            <div class="invoice-meta-row">
              <span class="invoice-meta-label">Issue Date</span>
              @if (isEditing()) {
                <input
                  type="date"
                  class="kt-input invoice-meta-input"
                  [ngModel]="form().issueDate"
                  (ngModelChange)="onFieldChange('issueDate', $event)"
                />
              } @else {
                <div class="invoice-readonly-value">{{ formatDate(current.issueDate) }}</div>
              }
            </div>
          </div>
        </header>

        <div class="invoice-parties">
          <div class="invoice-party">
            <div class="invoice-party-label">Billed From (Vendor)</div>
            @if (isEditing()) {
              <select class="kt-select" [ngModel]="form().vendorId" (ngModelChange)="onVendorChange($event)">
                @for (vendor of vendors(); track vendor.id) {
                  <option [value]="vendor.id">{{ vendor.name }}</option>
                }
              </select>
            } @else {
              <div class="invoice-readonly-value">{{ current.vendorName }}</div>
            }

            @if (selectedVendor()) {
              <div class="invoice-party-detail">
                <div>{{ selectedVendor()!.vendorTypeName }}</div>
                @if (selectedVendor()!.description) {
                  <div class="text-secondary-foreground">{{ selectedVendor()!.description }}</div>
                }
                <div class="text-secondary-foreground">Default currency: {{ selectedVendor()!.defaultCurrency }}</div>
              </div>
            }
          </div>

          <div class="invoice-party">
            <div class="invoice-party-label">{{ isEditing() ? 'Add linked records' : 'Reference' }}</div>
            @if (isEditing()) {
              <select class="kt-select" [ngModel]="form().linkType" (ngModelChange)="onLinkTypeChange($event)">
                <option value="Generic" disabled>Select a related record type...</option>
                <option value="Deal ID">Deal ID</option>
                <option value="Security ID">Security ID</option>
                <option value="Program">Program</option>
              </select>

              @if (form().linkType === 'Program') {
                <select
                  class="kt-select mt-2"
                  [ngModel]="form().linkedBondDocumentationId"
                  (ngModelChange)="onProgramChange($event)"
                >
                  <option [ngValue]="null">Select a program...</option>
                  @for (program of bondDocumentationOptions(); track program.id) {
                    <option [ngValue]="program.id">{{ program.name }}</option>
                  }
                </select>
              }

              <div class="invoice-party-detail">
                <div class="text-secondary-foreground">{{ linkSelectionSummary() }}</div>
              </div>
            } @else {
              <div class="invoice-readonly-value">{{ invoiceLinkSummary() }}</div>
              <div class="invoice-party-detail">
                <div class="text-secondary-foreground">{{ current.lines.length }} line(s)</div>
              </div>
            }
          </div>

          <div class="invoice-party invoice-party-amount">
            <div class="invoice-party-label">Amount</div>
            @if (isEditing()) {
              <div class="invoice-amount-row">
                <input
                  type="text"
                  class="kt-input invoice-ccy-input"
                  maxlength="3"
                  [ngModel]="form().currency"
                  (ngModelChange)="onFieldChange('currency', $event)"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  class="kt-input invoice-amount-input"
                  placeholder="0.00"
                  [ngModel]="form().amountText"
                  (ngModelChange)="onFieldChange('amountText', $event)"
                />
              </div>
              <div class="invoice-party-detail">
                <div class="text-secondary-foreground">Leave empty to use allocated total.</div>
              </div>
            } @else {
              <div class="invoice-readonly-strong">{{ formatAmount(current.currency, current.amount) }}</div>
            }
          </div>
        </div>

        <div class="invoice-lines">
          <div class="invoice-lines-header">
            <span>Invoice Lines</span>
            @if (isEditing()) {
              <div class="flex items-center gap-3">
                <span class="invoice-required-chip">Required</span>
                <button type="button" class="kt-btn kt-btn-sm kt-btn-outline" (click)="addGenericLine()">Add generic line</button>
                <button type="button" class="kt-btn kt-btn-sm kt-btn-outline" (click)="openLinkModal()">
                  Add linked records
                </button>
              </div>
            }
          </div>

          <div class="kt-table-wrapper kt-scrollable">
            <table class="kt-table">
              <thead>
                <tr>
                  <th scope="col">Concept</th>
                  <th scope="col">Linked To</th>
                  <th scope="col">Trade ID</th>
                  <th scope="col">Deal ID</th>
                  <th scope="col">Security ID</th>
                  <th scope="col">Program</th>
                  <th scope="col">Detail</th>
                  <th scope="col" class="text-end">Amount</th>
                  @if (isEditing()) {
                    <th scope="col" class="w-24"></th>
                  }
                </tr>
              </thead>
              <tbody>
                @if (isEditing()) {
                  @for (line of lineDrafts(); track line.key) {
                    <tr>
                      <td>
                        <select
                          class="kt-select kt-select-sm"
                          [ngModel]="line.invConceptId"
                          (ngModelChange)="onLineFieldChange(line.key, 'invConceptId', $event)"
                        >
                          <option [ngValue]="null">Select a concept...</option>
                          @for (concept of availableInvoiceConceptOptions(); track concept.id) {
                            <option [ngValue]="concept.id">{{ concept.name }}</option>
                          }
                        </select>
                      </td>
                      <td>{{ linkTypeLabel(line.linkTypeCode) }}</td>
                      <td>{{ line.tradeId || '-' }}</td>
                      <td>{{ line.dealId || '-' }}</td>
                      <td>{{ line.securityId }}</td>
                      <td>{{ line.bondDocumentationName || '-' }}</td>
                      <td>
                        <input
                          type="text"
                          class="kt-input kt-input-sm"
                          placeholder="Optional detail"
                          [ngModel]="line.detail"
                          (ngModelChange)="onLineFieldChange(line.key, 'detail', $event)"
                        />
                      </td>
                      <td class="text-end">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          class="kt-input kt-input-sm text-end w-32"
                          placeholder="0.00"
                          [ngModel]="line.amountText"
                          (ngModelChange)="onLineFieldChange(line.key, 'amountText', $event)"
                        />
                      </td>
                      <td class="text-end">
                        <div class="flex items-center justify-end gap-1">
                          @if (line.linkTypeCode !== 'NONE') {
                            <button
                              type="button"
                              class="kt-btn kt-btn-sm kt-btn-ghost"
                              title="Add another concept for this record"
                              (click)="duplicateLine(line.key)"
                            >+</button>
                          }
                          <button type="button" class="kt-btn kt-btn-sm kt-btn-ghost" (click)="removeLine(line.key)">Remove</button>
                        </div>
                      </td>
                    </tr>
                  } @empty {
                    <tr>
                      <td colspan="9" class="text-center text-secondary-foreground py-6">No invoice lines yet.</td>
                    </tr>
                  }
                } @else {
                  @for (line of current.lines; track line.id ?? line.lineNumber) {
                    <tr>
                      <td>{{ line.conceptName || '-' }}</td>
                      <td>{{ linkTypeLabel(line.linkTypeCode) }}</td>
                      <td>{{ line.tradeId || '-' }}</td>
                      <td>{{ line.dealId || '-' }}</td>
                      <td>{{ line.securityId }}</td>
                      <td>{{ line.bondDocumentationName || '-' }}</td>
                      <td>{{ line.detail || '-' }}</td>
                      <td class="text-end">{{ formatAmount(line.tradeCcy || current.currency, line.lineAmount) }}</td>
                    </tr>
                  } @empty {
                    <tr>
                      <td colspan="8" class="text-center text-secondary-foreground py-6">No invoice lines found.</td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        </div>

        <div class="invoice-lines">
          <div class="invoice-lines-header">
            <span>Invoice Documents</span>
            @if (isEditing()) {
              <span class="invoice-required-chip">Required</span>
            }
          </div>

          @if (isEditing()) {
            <div class="invoice-attachment-row">
              <input type="file" class="kt-input" [disabled]="isSaving()" (change)="onAttachmentSelected($any($event.target).files)" />
              @if (attachmentName()) {
                <div class="invoice-attachment-name">
                  <span>{{ attachmentName() }}</span>
                  @if (isSaving()) {
                    <span class="text-xs text-secondary-foreground">Uploading...</span>
                  } @else {
                    <button type="button" class="kt-btn kt-btn-sm kt-btn-ghost" (click)="clearAttachment()">Remove</button>
                  }
                </div>
              } @else {
                <div class="text-xs text-secondary-foreground">
                  Any document type is accepted. Upload a new file to replace the current one.
                </div>
              }
            </div>
          }

          <div class="kt-table-wrapper kt-scrollable mt-3">
            <table class="kt-table">
              <thead>
                <tr>
                  <th scope="col">Document Name</th>
                  <th scope="col">Uploaded At</th>
                  <th scope="col">Type</th>
                </tr>
              </thead>
              <tbody>
                @for (document of current.documents; track document.id) {
                  <tr>
                    <td>
                      @if (document.ezshareLink) {
                        <a class="kt-link" [href]="document.ezshareLink" target="_blank" rel="noopener noreferrer">
                          {{ document.fileName }}
                        </a>
                      } @else {
                        {{ document.fileName }}
                      }
                    </td>
                    <td>{{ document.createdAt ? formatDate(document.createdAt) : '-' }}</td>
                    <td>{{ document.type || '-' }}</td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="3" class="text-center text-secondary-foreground py-6">No invoice documents uploaded.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>

        <div class="invoice-optional">
          <div class="invoice-optional-header">
            <div>
              <div class="invoice-optional-title">Additional Details</div>
              <div class="invoice-optional-sub">These fields are not required to save the invoice.</div>
            </div>
            <span class="invoice-optional-chip">Optional</span>
          </div>

          <div class="invoice-optional-grid">
            <div class="field-block">
              <span class="field-label">Service Date Start</span>
              @if (isEditing()) {
                <input
                  type="date"
                  class="kt-input"
                  [ngModel]="form().serviceDateStart"
                  (ngModelChange)="onFieldChange('serviceDateStart', $event)"
                />
              } @else {
                <div class="invoice-readonly-value">{{ formatDate(current.serviceDateStart) }}</div>
              }
            </div>

            <div class="field-block">
              <span class="field-label">Service Date End</span>
              @if (isEditing()) {
                <input
                  type="date"
                  class="kt-input"
                  [ngModel]="form().serviceDateEnd"
                  (ngModelChange)="onFieldChange('serviceDateEnd', $event)"
                />
              } @else {
                <div class="invoice-readonly-value">{{ formatDate(current.serviceDateEnd) }}</div>
              }
            </div>

            <div class="field-block">
              <span class="field-label">Frequency</span>
              @if (isEditing()) {
                <select
                  class="kt-select"
                  [ngModel]="form().frequencyCode"
                  (ngModelChange)="onFieldChange('frequencyCode', $event)"
                >
                  <option value="">No frequency</option>
                  @for (option of frequencyOptions(); track option.code) {
                    <option [value]="option.code">{{ option.name }}</option>
                  }
                </select>
              } @else {
                <div class="invoice-readonly-value">{{ current.frequency || current.frequencyCode || '-' }}</div>
              }
            </div>

          </div>
        </div>

        @if (isEditing()) {
          <div class="invoice-optional">
            <div class="invoice-optional-header">
              <div>
                <div class="invoice-optional-title">Email Recipients</div>
                <div class="invoice-optional-sub">Add multiple email addresses separated by commas or semicolons.</div>
              </div>
              <span class="invoice-optional-chip">Optional</span>
            </div>
            <label class="field-block">
              <span class="field-label">Recipients</span>
              <input
                type="email"
                multiple
                class="kt-input"
                placeholder="name@example.com, another@example.com"
                [ngModel]="form().recipientEmail"
                (ngModelChange)="onFieldChange('recipientEmail', $event)"
              />
              <span class="text-xs text-secondary-foreground">Leave empty to use the configured default recipients.</span>
            </label>
          </div>
        }

        <footer class="invoice-sheet-footer">
          <div class="invoice-total-box">
            <span class="invoice-total-label">Invoice Total</span>
            <span class="invoice-total-value">
              @if (isEditing()) {
                {{ formatAmount(form().currency, enteredInvoiceTotal()) }}
              } @else {
                {{ formatAmount(current.currency, current.amount) }}
              }
            </span>
          </div>

          @if (isEditing()) {
            <div class="flex items-center gap-2">
              <button type="button" class="kt-btn kt-btn-outline" [disabled]="isSaving()" (click)="cancelEditing()">Cancel</button>
              <button type="button" class="kt-btn kt-btn-outline" [disabled]="isSaving()" (click)="sendEmailOnly()">
                @if (savingAction() === 'email') {
                  <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                  Sending...
                } @else {
                  Send Email
                }
              </button>
              <button type="button" class="kt-btn kt-btn-outline" [disabled]="isSaving()" (click)="saveInvoiceEdits()">
                @if (savingAction() === 'save') {
                  <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                  Saving...
                } @else {
                  Save changes
                }
              </button>
              <button type="button" class="kt-btn kt-btn-primary" [disabled]="isSaving()" (click)="saveAndSendEmail()">
                @if (savingAction() === 'save-send') {
                  <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                  Saving & Sending...
                } @else {
                  Save & Send
                }
              </button>
            </div>
          } @else {
            <div class="flex items-center gap-2">
              <button type="button" class="kt-btn kt-btn-outline" [disabled]="isSaving()" (click)="sendEmailOnly()">
                @if (savingAction() === 'email') {
                  <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                  Sending...
                } @else {
                  Send Email
                }
              </button>
              <button
                type="button"
                class="kt-btn kt-btn-primary"
                [disabled]="isLoadingEditOptions()"
                (click)="startEditing()"
              >
                @if (isLoadingEditOptions()) {
                  <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                  Loading editor...
                } @else {
                  Edit Invoice
                }
              </button>
            </div>
          }
        </footer>

        <div class="invoice-audit-strip">
          <div class="invoice-audit-item">
            <span class="invoice-audit-label">Added At</span>
            <span>{{ formatDate(current.createdAt) }}</span>
          </div>
          <div class="invoice-audit-item">
            <span class="invoice-audit-label">Created By</span>
            <span>{{ current.createdBy || '-' }}</span>
          </div>
          <div class="invoice-audit-item">
            <span class="invoice-audit-label">Last Modified By</span>
            <span>{{ current.lastModifiedBy || '-' }}</span>
          </div>
        </div>
      </section>
    }
  </div>
</main>

@if (isLinkModalOpen()) {
  <app-related-records-selector
    [linkTypeCode]="selectedLinkTypeCode()"
    [bondDocumentationId]="form().linkedBondDocumentationId"
    [initialSelections]="modalInitialSelections()"
    (cancelled)="closeLinkModal()"
    (saved)="saveLinkModalSelection($event)"
  />
}
